import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import {
  containsTarget,
  elementById,
  focusElement,
  inputValue,
  onDocumentPointerDown,
  prefersReducedMotion,
  scrollChildIntoView,
} from "../lib/dom";

export interface ListboxOption<T extends string> {
  value: T;
  label: string;
  /** Decorative tile before the label, on the trigger and in the list. */
  lead: ReactNode;
  detail?: string;
}

export interface ListboxProps<T extends string> {
  id: string;
  name: string;
  label: string;
  value: T;
  options: readonly ListboxOption<T>[];
  onChange(value: T): void;
  /**
   * False for the server render and the hydration pass, which show a native select so the
   * choice works without JS. The island flips it after mount.
   */
  enhanced: boolean;
  /** The form the native select submits with, when it sits outside that form. */
  form?: string;
  className?: string;
}

const TYPEAHEAD_RESET_MS = 600;

/**
 * A select-only combobox (WAI-ARIA APG): focus stays on the trigger and
 * aria-activedescendant names the highlighted option.
 */
export function Listbox<T extends string>(props: ListboxProps<T>) {
  const { id, name, label, value, options, onChange, enhanced, form, className } = props;
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const selected = options[selectedIndex];
  const labelId = `${id}-label`;
  const listId = `${id}-list`;
  const optionId = (index: number) => `${id}-opt-${index}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(selectedIndex);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const typed = useRef({ text: "", at: 0 });
  // Space selects on keydown; some browsers still send the button's click on keyup.
  const swallowClick = useRef(false);

  useEffect(() => {
    if (!open) return;
    return onDocumentPointerDown((target) => {
      if (!containsTarget(rootRef.current, target)) setOpen(false);
    });
  }, [open]);

  // On open, bring the whole list into the window: it opens downwards and can run past the
  // bottom when the trigger sits low.
  useEffect(() => {
    if (!open) return;
    elementById(listId)?.scrollIntoView?.({
      block: "nearest",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [listId, open]);

  // Keyboard moves scroll the list only, so they never cut that page scroll short.
  useEffect(() => {
    if (open) scrollChildIntoView(elementById(listId), elementById(`${id}-opt-${active}`));
  }, [id, listId, open, active]);

  const openAt = (index: number) => {
    setActive(index);
    setOpen(true);
  };

  const choose = (index: number) => {
    setOpen(false);
    const option = options[index];
    if (option && option.value !== value) onChange(option.value);
  };

  const typeahead = (char: string) => {
    const now = Date.now();
    const text = now - typed.current.at > TYPEAHEAD_RESET_MS ? char : typed.current.text + char;
    typed.current = { text, at: now };
    const start = open ? active : selectedIndex;
    const ordered = [...options.keys()].map(
      (i) => (start + (text.length === 1 ? 1 : 0) + i) % options.length,
    );
    const match = ordered.find((i) =>
      options[i].label.toLowerCase().startsWith(text.toLowerCase()),
    );
    if (match === undefined) return;
    if (open) setActive(match);
    else openAt(match);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    swallowClick.current = false;
    const last = options.length - 1;
    const typing = Date.now() - typed.current.at <= TYPEAHEAD_RESET_MS && typed.current.text !== "";
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (open) setActive(Math.min(active + 1, last));
        else openAt(selectedIndex);
        return;
      case "ArrowUp":
        event.preventDefault();
        if (open) setActive(Math.max(active - 1, 0));
        else openAt(selectedIndex);
        return;
      case "Home":
        event.preventDefault();
        openAt(0);
        return;
      case "End":
        event.preventDefault();
        openAt(last);
        return;
      case "Enter":
        if (!open) return;
        event.preventDefault();
        choose(active);
        return;
      case " ":
        if (typing) {
          event.preventDefault();
          typeahead(" ");
          return;
        }
        if (!open) return;
        event.preventDefault();
        swallowClick.current = true;
        choose(active);
        return;
      case "Escape":
        if (!open) return;
        event.preventDefault();
        setOpen(false);
        return;
      case "Tab":
        if (open) choose(active);
        return;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          typeahead(event.key);
        }
    }
  };

  const control = enhanced ? (
    <button
      ref={triggerRef}
      type="button"
      id={id}
      role="combobox"
      className="lb-control"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={listId}
      aria-labelledby={`${labelId} ${id}-value`}
      aria-activedescendant={open ? optionId(active) : undefined}
      onPointerDown={() => {
        swallowClick.current = false;
      }}
      onClick={() => {
        if (swallowClick.current) {
          swallowClick.current = false;
          return;
        }
        if (open) setOpen(false);
        else openAt(selectedIndex);
        focusElement(triggerRef.current);
      }}
      onKeyDown={onKeyDown}
      onKeyUp={() => {
        // The keyup click, when a browser sends one, has fired by now.
        if (swallowClick.current) setTimeout(() => (swallowClick.current = false), 0);
      }}
      onBlur={() => setOpen(false)}
    >
      <span id={`${id}-value`} className="lb-value">
        {selected?.label}
      </span>
    </button>
  ) : (
    <select
      id={id}
      name={name}
      form={form}
      className="lb-control"
      value={value}
      onChange={(event) => {
        const next = options.find((option) => option.value === inputValue(event));
        if (next) onChange(next.value);
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className={className} ref={rootRef}>
      <label
        id={labelId}
        className="label"
        htmlFor={id}
        // Without this the trigger blurs, closes, then reopens from the label's forwarded click.
        onMouseDown={enhanced ? (event) => event.preventDefault() : undefined}
      >
        {label}
      </label>
      <div className="lb-field mt-1.5">
        <span className="lb-lead" aria-hidden="true">
          {selected?.lead}
        </span>
        {control}
        {enhanced ? (
          <ul
            role="listbox"
            id={listId}
            aria-labelledby={labelId}
            tabIndex={-1}
            className="lb-list"
            hidden={!open}
            // Keeps focus on the trigger, so a mouse or touch press on an option does not blur
            // it and close the list before the option's click lands.
            onPointerDown={(event) => event.preventDefault()}
            onMouseDown={(event) => event.preventDefault()}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={index === selectedIndex}
                data-active={index === active ? "" : undefined}
                className="lb-option"
                onMouseMove={() => {
                  if (index !== active) setActive(index);
                }}
                onClick={() => {
                  choose(index);
                  focusElement(triggerRef.current, true);
                }}
              >
                <span aria-hidden="true">{option.lead}</span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-ink">{option.label}</span>
                  {option.detail ? (
                    <span className="mt-0.5 block text-[13px] font-normal text-neutral-600">
                      {option.detail}
                    </span>
                  ) : null}
                </span>
                <i
                  className={`fa-solid fa-check lb-check ${index === selectedIndex ? "" : "invisible"}`}
                  aria-hidden="true"
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
